import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { NewLayout } from '@/components/NewLayout';
import { Redirects } from '@/components/Redirects';
import AccountSettings from '@/pages/account-settings/AccountSettings';
import { AdvancedEditorPage } from '@/pages/AdvancedEditor/AdvancedEditorPage';
import { AgentEditorPage, AgentInsightsPage, AgentsPage, AgentTemplatePage } from '@/pages/agents/pages';
import { AppsPage } from '@/pages/apps';
import { CampaignCreatePage, CampaignPage } from '@/pages/campaign';
import Checkout from '@/pages/checkout/Checkout';
import { HomePage } from '@/pages/home';
import PostCheckout from '@/pages/post-checkout/PostCheckout';
import SupportPage from '@/pages/support/SupportPage';
import { VideoStudioPage } from '@/pages/video-studio';
import { useFlag } from '@/services/featureFlags';
import { AppRoutes } from '@/utils/types';
import { ReactNode } from 'react';
import { Navigate, Routes as ReactRouterRoutes, Route, useLocation } from 'react-router-dom';
import {
    AvatarCreateExpressPage,
    AvatarCreatePage,
    AvatarCreateSuccessPage,
    AvatarEditPage,
    AvatarListPage,
    PremiumPlusAvatarCreatePage,
    PremiumPlusAvatarCreateSuccessPage,
} from '../pages/avatar';
import { BackofficePage } from '../pages/backoffice';
import { useTranslateProofreadAccess } from '../pages/translation/hooks/useTranslateProofreadAccess';
import TranslationDraftPage from '../pages/translation/TranslationDraftPage/TranslationDraftPage';
import { TranslationLibraryPage } from '../pages/translation/TranslationLibraryPage';
import { AuthGuard } from './AuthGuard';

export interface RouteType {
    path: string;
    tag: string;
    component: ReactNode;
    layout?: any;
    title?: string;
}

export const ProtectedRoutes = () => {
    const location = useLocation();
    const dvpFlowIsEnabled = useFlag('dvp_creation_is_enabled', false);
    const { proofreadEnabled } = useTranslateProofreadAccess();
    const backofficeEnabled = useFlag('backoffice_is_enabled', false);

    const routes: RouteType[] = [
        { path: AppRoutes.Home, tag: 'Home', component: <HomePage />, layout: NewLayout, title: 'Home - D-ID' },
        {
            path: AppRoutes.VideoStudio,
            tag: 'VideoStudio',
            component: <VideoStudioPage />,
            layout: NewLayout,
            title: 'Videos - D-ID',
        },
        {
            path: AppRoutes.VideoLibrary,
            tag: 'VideoLibrary',
            component: <Navigate to={AppRoutes.VideoStudio + location.search} state={location.state} />,
            layout: NewLayout,
        },
        { path: AppRoutes.Agent, tag: 'Agents', component: <AgentsPage />, layout: NewLayout, title: 'Agents - D-ID' },
        {
            path: AppRoutes.AgentTemplate + '/:agentId',
            tag: 'Agent Template',
            component: <AgentTemplatePage />,
            layout: NewLayout,
            title: 'Agent Template - D-ID',
        },
        { path: AppRoutes.Insights, tag: 'Insights', component: <AgentInsightsPage />, layout: NewLayout, title: 'Agents Insights - D-ID' },
        {
            path: AppRoutes.AccountSettings,
            tag: 'AccountSettings',
            component: <AccountSettings />,
            layout: NewLayout,
            title: 'Profile - D-ID',
        },
        {
            path: AppRoutes.Campaigns,
            tag: 'Campaign',
            component: <CampaignPage />,
            layout: NewLayout,
            title: 'Video Campaigns - D-ID',
        },
        { path: AppRoutes.CampaignCreate, tag: 'Campaign Create', component: <CampaignCreatePage /> },
        { path: AppRoutes.Avatars, tag: 'Avatars', component: <AvatarListPage />, layout: NewLayout, title: 'Avatars - D-ID' },
        { path: AppRoutes.AvatarsCreate, tag: 'Avatars Create', component: <AvatarCreatePage /> },
        { path: AppRoutes.AvatarsCreateSuccess, tag: 'Avatars Create Success', component: <AvatarCreateSuccessPage /> },
        { path: AppRoutes.AvatarEditPage, tag: 'Avatars Edit', component: <AvatarEditPage /> },
        {
            path: AppRoutes.AvatarsCreateExpress,
            tag: 'Choose Video Avatars',
            component: <AvatarCreateExpressPage />,
        },
        ...(dvpFlowIsEnabled
            ? [
                  {
                      path: AppRoutes.AvatarsCreatePremiumPlus,
                      tag: 'Premium Plus Avatars Create',
                      component: <PremiumPlusAvatarCreatePage />,
                  },
                  {
                      path: AppRoutes.AvatarCreatePremiumPlusSuccessPage,
                      tag: 'Premium Plus Avatars Create Success',
                      component: <PremiumPlusAvatarCreateSuccessPage />,
                  },
              ]
            : []),
        { path: AppRoutes.Apps, tag: 'Apps', component: <AppsPage />, layout: NewLayout, title: 'Apps - D-ID' },
        ...(backofficeEnabled ? [{ path: AppRoutes.Backoffice, tag: 'Backoffice', component: <BackofficePage />, layout: NewLayout }] : []),
        { path: AppRoutes.AgentEditor + '/:agentId', tag: 'Agent Edit', component: <AgentEditorPage /> },
        { path: AppRoutes.Checkout, tag: 'Checkout', component: <Checkout /> },
        { path: AppRoutes.PostCheckout, tag: 'Checkout', component: <PostCheckout /> },
        {
            path: AppRoutes.Translation,
            tag: 'Translation',
            component: <TranslationLibraryPage />,
            layout: NewLayout,
            title: 'Video Translate - D-ID',
        },
        ...(proofreadEnabled
            ? [
                  {
                      path: AppRoutes.TranslationDraft + '/*',
                      tag: 'Edit Translation',
                      component: <TranslationDraftPage />,
                  },
              ]
            : []),
        { path: AppRoutes.AdvancedEditor + '/:projectId', tag: 'AdvancedEditor', component: <AdvancedEditorPage /> },
        { path: AppRoutes.Support, tag: 'Support', component: <SupportPage />, layout: NewLayout, title: 'Support - D-ID' },
        { path: '*', tag: '', component: <Navigate to={AppRoutes.Home} state={location.state} />, layout: NewLayout },
    ];

    const renderComponent = (route: RouteType) => (
        <ErrorBoundary fallback={<Navigate to="/" />} beforeCapture={scope => scope.setTag('component', route.tag)}>
            <AuthGuard>
                {route.layout ? <route.layout title={route.title}>{route.component}</route.layout> : <>{route.component}</>}
            </AuthGuard>
        </ErrorBoundary>
    );

    return (
        <ReactRouterRoutes>
            <Route element={<Redirects />}>
                {routes.map(route => (
                    <Route key={route.path} path={route.path} element={renderComponent(route)} />
                ))}
            </Route>
        </ReactRouterRoutes>
    );
};
